import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { SelectField, Button } from "@makula/ui";

// ─── Types ────────────────────────────────────────────────────────────────────

type QuoteType = "Service Quotation" | "Spare Part Quotation" | "Asset Quotation" | "Installation Quotation";
type QuoteStatus = "PENDING" | "ACCEPTED" | "REJECTED" | "DRAFT" | "SENT";
type TabFilter = "All" | "Draft" | "Pending" | "Sent" | "Accepted" | "Rejected";

interface Quotation {
  id: string;
  number: string;
  title: QuoteType;
  company: string;
  status: QuoteStatus;
  amount: string;
  issueDate: string;
  createdBy: string;
  createdByInitials: string;
}

// ─── Sample Data ──────────────────────────────────────────────────────────────

const MOCK_QUOTATIONS: Quotation[] = [
  { id: "1", number: "QT-2026-0001", title: "Service Quotation",     company: "Acme Internationals", status: "PENDING",  amount: "1.000,00 €", issueDate: "12 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "2", number: "QT-2026-0001", title: "Spare Part Quotation",  company: "Acme Internationals", status: "ACCEPTED", amount: "1.000,00 €", issueDate: "12 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "3", number: "QT-2026-0001", title: "Installation Quotation",company: "Acme Internationals", status: "REJECTED", amount: "1.000,00 €", issueDate: "12 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "4", number: "QT-2026-0001", title: "Installation Quotation",company: "Acme Internationals", status: "DRAFT",    amount: "1.000,00 €", issueDate: "12 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "5", number: "QT-2026-0002", title: "Installation Quotation",company: "Beta Solutions",      status: "SENT",     amount: "2.500,00 €", issueDate: "15 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "6", number: "QT-2026-0002", title: "Spare Part Quotation",  company: "Beta Solutions",      status: "ACCEPTED", amount: "2.500,00 €", issueDate: "15 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "7", number: "QT-2026-0002", title: "Spare Part Quotation",  company: "Beta Solutions",      status: "REJECTED", amount: "2.500,00 €", issueDate: "15 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "8", number: "QT-2026-0002", title: "Service Quotation",     company: "Beta Solutions",      status: "DRAFT",    amount: "2.500,00 €", issueDate: "15 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "9", number: "QT-2026-0003", title: "Service Quotation",     company: "Gamma Enterprises",   status: "SENT",     amount: "5.000,00 €", issueDate: "20 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "10",number: "QT-2026-0003", title: "Service Quotation",     company: "Gamma Enterprises",   status: "ACCEPTED", amount: "5.000,00 €", issueDate: "20 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "11",number: "QT-2026-0003", title: "Asset Quotation",       company: "Gamma Enterprises",   status: "REJECTED", amount: "5.000,00 €", issueDate: "20 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "12",number: "QT-2026-0004", title: "Service Quotation",     company: "Delta Corp",          status: "DRAFT",    amount: "8.750,00 €",  issueDate: "22 Apr 2026", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
  { id: "13",number: "QT-2026-0005", title: "Spare Part Quotation",  company: "Heartland Coca-Cola", status: "SENT",     amount: "11.224,18 €", issueDate: "12 Nov 2025", createdBy: "Micheal Zeigler", createdByInitials: "MZ" },
];

const SAMPLE_WORK_ORDERS = [
  "WO-2026-0045 – Routine Maintenance",
  "WO-2026-0044 – Pump Replacement",
  "WO-2026-0043 – Annual Inspection",
  "WO-2026-0042 – Filter Change",
  "WO-2026-0041 – Emergency Repair",
];

const SAMPLE_COMPANIES = [
  "Acme Internationals",
  "Beta Solutions",
  "Gamma Enterprises",
  "Delta Corp",
  "Epsilon Industries",
  "Zeta Manufacturing",
];

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<QuoteStatus, { bg: string; color: string }> = {
  PENDING:  { bg: "#fff0e6", color: "#FF7A00" },
  ACCEPTED: { bg: "#e6f9f1", color: "#10B981" },
  REJECTED: { bg: "#fde5f0", color: "#ee0064" },
  DRAFT:    { bg: "#f3f4f6", color: "#748094" },
  SENT:     { bg: "#e6e8fe", color: "#0517f8" },
};

function StatusBadge({ status }: { status: QuoteStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px 6px",
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "-0.24px",
        background: cfg.bg,
        color: cfg.color,
        fontFamily: "var(--font-family-base)",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <path d="M6 2L8.5 5H3.5L6 2Z" fill="currentColor" />
      <path d="M6 10L3.5 7H8.5L6 10Z" fill="currentColor" />
    </svg>
  );
}

// ─── Column icons ─────────────────────────────────────────────────────────────

function IdIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="11" height="9" rx="1.5" />
      <path d="M4.5 5.5h5M4.5 7h3" />
    </svg>
  );
}

function TitleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h10M2 6h7M2 9h5" />
    </svg>
  );
}

function CompanyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="10" height="8" rx="1" />
      <path d="M5 4V3a2 2 0 014 0v1M5 8h4" />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5" />
      <path d="M7 4.5v3l2 1" />
    </svg>
  );
}

function AmountIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5" />
      <path d="M7 4v6M5.5 5.5a1.5 1.5 0 013 0c0 1-3 1.5-3 3a1.5 1.5 0 003 0" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" />
      <path d="M1.5 6h11M4.5 1v3M9.5 1v3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="5" r="2.5" />
      <path d="M2 12c0-2.761 2.239-5 5-5s5 2.239 5 5" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="3.5" cy="8" r="1.25" />
      <circle cx="8" cy="8" r="1.25" />
      <circle cx="12.5" cy="8" r="1.25" />
    </svg>
  );
}


function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

// ─── New Quote Drawer ─────────────────────────────────────────────────────────

interface NewQuoteDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreateQuote: (data: { company: string; workOrder: string }) => void;
}

function NewQuoteDrawer({ open, onClose, onCreateQuote }: NewQuoteDrawerProps) {
  const [company, setCompany] = React.useState("Acme International");
  const [workOrder, setWorkOrder] = React.useState("");

  function handleCreate() {
    onCreateQuote({ company, workOrder });
    onClose();
  }

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 100 }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 360,
        height: "100vh",
        background: "var(--color-bg-primary)",
        zIndex: 101,
        display: "flex",
        flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
            New Quote
          </span>
          <button
            onClick={onClose}
            style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, color: "var(--color-content-secondary)" }}
          >
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 8px" }}>
          {/* Company */}
          <SelectField
            label="Company"
            required
            value={company}
            placeholder="Select a company"
            onChange={setCompany}
            options={SAMPLE_COMPANIES.map((c) => ({ label: c, value: c }))}
            style={{ marginBottom: 20 }}
          />

          {/* Link Work Order */}
          <SelectField
            label="Link Work Order"
            value={workOrder}
            placeholder="Select a work order"
            onChange={setWorkOrder}
            options={SAMPLE_WORK_ORDERS.map((wo) => ({ label: wo, value: wo }))}
            description="The quote will be accessible from within the work order or ticket thread"
          />
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--color-border-primary)", flexShrink: 0 }}>
          <Button
            variant="primary"
            size="lg"
            onClick={handleCreate}
            style={{ width: "100%" }}
          >
            Create Quote
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface QuotationsListPageProps {
  onOpenQuotation: (id: string) => void;
  onCreateNew: () => void;
}

const TABS: TabFilter[] = ["All", "Draft", "Pending", "Sent", "Accepted", "Rejected"];

export function QuotationsListPage({ onOpenQuotation, onCreateNew: _onCreateNew }: QuotationsListPageProps) {
  const [activeTab, setActiveTab] = React.useState<TabFilter>("All");
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
  const [sortCol, setSortCol] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const tabCounts: Record<TabFilter, number> = {
    All: MOCK_QUOTATIONS.length,
    Draft: MOCK_QUOTATIONS.filter((q) => q.status === "DRAFT").length,
    Pending: MOCK_QUOTATIONS.filter((q) => q.status === "PENDING").length,
    Sent: MOCK_QUOTATIONS.filter((q) => q.status === "SENT").length,
    Accepted: MOCK_QUOTATIONS.filter((q) => q.status === "ACCEPTED").length,
    Rejected: MOCK_QUOTATIONS.filter((q) => q.status === "REJECTED").length,
  };

  const filteredRows = React.useMemo(() => {
    let rows = [...MOCK_QUOTATIONS];
    if (activeTab !== "All") {
      rows = rows.filter((q) => q.status === activeTab.toUpperCase());
    }
    if (sortCol) {
      rows.sort((a, b) => {
        const va = ((a as unknown) as Record<string, unknown>)[sortCol] as string ?? "";
        const vb = ((b as unknown) as Record<string, unknown>)[sortCol] as string ?? "";
        return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      });
    }
    return rows;
  }, [activeTab, sortCol, sortDir]);

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  function handleCreateQuote(data: { company: string; workOrder: string }) {
    console.log("Create quote:", data);
    _onCreateNew();
  }

  const colHeaderStyle: React.CSSProperties = {
    background: "var(--color-bg-accent)",
    borderBottom: "1px solid var(--color-border-primary)",
    padding: "0 12px",
    height: 34,
    whiteSpace: "nowrap",
    textAlign: "left",
    fontWeight: "normal",
  };

  const colHeaderInner: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    color: "#748094",
    cursor: "pointer",
    userSelect: "none",
    fontFamily: "var(--font-family-base)",
  };

  const cellStyle: React.CSSProperties = {
    padding: "0 12px",
    height: 48,
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-content-primary)",
    fontFamily: "var(--font-family-base)",
    borderBottom: "1px solid var(--color-border-primary)",
    verticalAlign: "middle",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Page header */}
      <PageHeader
        breadcrumbs={[{ label: "Quotations" }]}
        searchAction={() => {}}
        primaryAction={{ label: "New Quote", onClick: () => setDrawerOpen(true) }}
      />

      <div style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-primary)" }}>

        {/* Content card */}
        <div style={{ margin: "24px 24px 24px", border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>

          {/* Tabs */}
          <div style={{ borderBottom: "1px solid var(--color-border-primary)", padding: "16px 0 0" }}>
            <div style={{ display: "flex", gap: 0 }}>
              {TABS.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: "0 8px 12px",
                      marginRight: 4,
                      border: "none",
                      borderBottom: active ? "2px solid var(--color-bg-brand-dark)" : "2px solid transparent",
                      background: "transparent",
                      cursor: "pointer",
                      fontFamily: "var(--font-family-base)",
                      fontSize: 12,
                      fontWeight: 700,
                      color: active ? "var(--color-content-primary)" : "#a2aab8",
                      whiteSpace: "nowrap",
                      letterSpacing: "-0.24px",
                      lineHeight: "16px",
                      transition: "color 120ms ease, border-color 120ms ease",
                    }}
                  >
                    {tab} ({tabCounts[tab]})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 132 }} />
                <col style={{ width: 182 }} />
                <col style={{ width: 119 }} />
                <col style={{ width: 119 }} />
                <col style={{ width: 158 }} />
                <col style={{ width: 158 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "number",    label: "Quote",      icon: <IdIcon />,       sortable: false },
                    { key: "company",   label: "Company",    icon: <CompanyIcon />,  sortable: true  },
                    { key: "status",    label: "Status",     icon: <StatusIcon />,   sortable: false },
                    { key: "amount",    label: "Amount",     icon: <AmountIcon />,   sortable: true  },
                    { key: "issueDate", label: "Created Date", icon: <CalendarIcon />, sortable: true  },
                    { key: "createdBy", label: "Created by", icon: <UserIcon />,     sortable: false },
                    { key: "__actions", label: "",           icon: null,             sortable: false },
                  ].map(({ key, label, icon, sortable }) => (
                    <th key={key} style={{ ...colHeaderStyle }}>
                      {label ? (
                        <span
                          style={colHeaderInner}
                          onClick={() => sortable && handleSort(key)}
                        >
                          {icon}
                          {label}
                          {sortable && <SortIcon />}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      background: hoveredRow === row.id ? "var(--color-bg-accent)" : "var(--color-bg-primary)",
                      cursor: "pointer",
                      transition: "background 80ms ease",
                    }}
                    onClick={() => onOpenQuotation(row.id)}
                  >
                    {/* Quote number */}
                    <td style={{ ...cellStyle }}>{row.number}</td>
                    {/* Company */}
                    <td style={cellStyle}>{row.company}</td>
                    {/* Status */}
                    <td style={cellStyle}>
                      <StatusBadge status={row.status} />
                    </td>
                    {/* Amount */}
                    <td style={cellStyle}>{row.amount}</td>
                    {/* Issue date */}
                    <td style={{ ...cellStyle, color: "#748094" }}>{row.issueDate}</td>
                    {/* Created by */}
                    <td style={cellStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{
                          width: 22, height: 22,
                          borderRadius: "50%",
                          background: "#0517f8",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontFamily: "var(--font-family-base)",
                          letterSpacing: "-0.2px",
                        }}>
                          {row.createdByInitials}
                        </div>
                        <span>{row.createdBy}</span>
                      </div>
                    </td>
                    {/* Actions */}
                    <td
                      style={{ ...cellStyle, overflow: "visible", textOverflow: "unset", textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button style={{ border: "none", background: "transparent", cursor: "pointer", color: "#748094", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 4, borderRadius: 4 }}>
                        <DotsIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRows.length === 0 && (
              <div style={{
                textAlign: "center",
                padding: "48px 24px",
                color: "var(--color-content-tertiary)",
                fontSize: 14,
                fontFamily: "var(--font-family-base)",
              }}>
                No quotations found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Quote Drawer */}
      <NewQuoteDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreateQuote={handleCreateQuote}
      />
    </div>
  );
}
